import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    HbrButton,
    HbrDivider,
    HbrDomTable,
    HbrIcon,
    HbrPagination,
    HbrSpinner,
} from '@harbor/elements-react-standalone';
import { caretRight } from '@harbor/elements/icons';

import {
    Column,
    ColumnAlignment,
    ColumnPin,
    ColumnType,
    DataType,
    getColumnTypeConfig,
} from '../../../types/table';
import { OpportunityPhaseLabel } from '../../../types/opportunity';

import { useSfTeamOpportunities } from '../../../hooks/useSfTeamOpportunities';
import { fetchItems } from '../../../support/queries';
import { useFetchOpportunityActivityHistory } from '../../../hooks/opportunity-hooks';
import StbModal from '../../security-toolbox-ui/StbModal';

type ChipColor = 'red' | 'yellow' | 'green';

function safeRowId(s: string) {
    return String(s || '').replace(/[^a-z0-9_-]/gi, '_');
}

function formatCurrencyUSD(n: any) {
    const v = Number(n);
    if (!Number.isFinite(v)) return '';
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(v);
}

function formatDate(v: any) {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('en-US');
}

// Age-based chip color
function getAgeChipColor(lastUpdated: any): ChipColor {
    if (!lastUpdated) return 'green';
    const d = new Date(lastUpdated);
    if (Number.isNaN(d.getTime())) return 'green';

    const days = (Date.now() - d.getTime()) / 86_400_000;
    if (days >= 30) return 'red';
    if (days >= 7) return 'yellow';
    return 'green';
}

function chipClass(color: ChipColor) {
    if (color === 'red') return 'bg-danger';
    if (color === 'yellow') return 'bg-warning';
    return 'bg-success';
}

function formatSeNotes(seNotes: string | null | undefined) {
    if (!seNotes) return [];
    const entries = String(seNotes).match(/^\d{4}-\d{2}-\d{2}.*$/gm) || [];
    return entries.map((entry) => {
        const [date, name, token, ...rest] = entry.split(' - ');
        if (Object.values(OpportunityPhaseLabel).includes(token as any)) {
            return {
                date,
                text: ` - ${token} - ${name}${rest && rest.length > 0 ? ` - ${rest.join(' - ')}` : ''}`,
            };
        }
        return {
            date,
            text: ` - ${name} - ${token}${rest && rest.length > 0 ? ` - ${rest.join(' - ')}` : ''}`,
        };
    });
}

const SEWorkLogPanel: React.FC<{ opportunityId: string }> = ({ opportunityId }) => {
    const { data: oppActivities, isLoading } = useFetchOpportunityActivityHistory(
        { opportunity: opportunityId },
        { enabled: !!opportunityId }
    );

    const [selectedActivity, setSelectedActivity] = useState<any | null>(null);
    const [activityOwner, setActivityOwner] = useState<{ Name: string; Email: string } | null>(null);

    const fetchActivityOwner = async (ownerId: string) => {
        const ownerData = await fetchItems(`sf/query`, {
            q: `SELECT Name, Email FROM User WHERE Id = '${ownerId}'`,
        });
        return ownerData?.[0];
    };

    const handleRowClick = async (activity: any) => {
        setSelectedActivity(activity);
        setActivityOwner(null);
        if (activity?.OwnerId) {
            const ownerDetails = await fetchActivityOwner(activity.OwnerId);
            setActivityOwner(ownerDetails ?? null);
        }
    };

    const handleCloseModal = async () => {
        setSelectedActivity(null);
        setActivityOwner(null);
    };

    return (
        <div>
            <div className="hbr-css__text-primary-sm-semibold mb-2">Work History</div>

            {isLoading && <HbrSpinner size="medium" />}

            {!isLoading && (
                <div className="max-h-80 overflow-y-auto mb-4">
                    <div className="grid grid-cols-3">
                        <div className="text-sm font-medium">Status</div>
                        <div className="text-sm font-medium">Date</div>
                        <div className="text-sm font-medium">Summary</div>
                    </div>

                    {(oppActivities ?? []).map((activity: any, idx: number) => (
                        <div
                            key={idx}
                            className="cursor-pointer grid-cols-3 grid hover:bg-gray-200"
                            onClick={() => handleRowClick(activity)}
                        >
                            <div className="text-sm my-1">{activity.Status}</div>
                            <div className="text-sm my-1">{activity.ActivityDate}</div>
                            <div className="text-sm my-1">{activity.Subject}</div>
                        </div>
                    ))}
                </div>
            )}

            <StbModal
                role="information"
                title="Activity Details"
                open={!!selectedActivity}
                onSubmit={() => Promise.resolve()}
                onClose={handleCloseModal}
            >
                <>
                    <div className="grid grid-cols-[6rem_1fr] gap-x-4">
                        <div className="text-sm font-semibold mb-2">Status:</div>
                        <div className="text-sm text-gray-700 mb-2">{selectedActivity?.Status ?? ''}</div>

                        <div className="text-sm font-semibold mb-2">Date:</div>
                        <div className="text-sm text-gray-700 mb-2">{selectedActivity?.ActivityDate ?? ''}</div>

                        <div className="text-sm font-semibold mb-2">Summary:</div>
                        <div className="text-sm text-gray-700 mb-2">{selectedActivity?.Subject ?? ''}</div>

                        <div className="text-sm font-semibold mb-2">Priority:</div>
                        <div className="text-sm text-gray-700 mb-2">{selectedActivity?.Priority ?? ''}</div>
                    </div>

                    <div className="text-sm font-semibold">Description:</div>
                    {selectedActivity?.Description ? (
                        <textarea
                            value={selectedActivity.Description}
                            readOnly
                            className="text-sm text-gray-700 w-full min-h-24 my-2 border border-gray-300 p-2 rounded"
                        />
                    ) : (
                        <div className="text-sm text-gray-700 w-full min-h-24 my-2 italic border border-gray-300 p-2 rounded">
                            No description available
                        </div>
                    )}

                    {activityOwner ? (
                        <div className="grid grid-cols-[6rem_1fr] gap-x-4">
                            <div className="text-sm font-semibold mb-2">Owner:</div>
                            <div className="text-sm text-gray-700 mb-2">{activityOwner?.Name ?? ''}</div>

                            <div className="text-sm font-semibold mb-2">Email:</div>
                            <div className="text-sm text-gray-700 mb-2">{activityOwner?.Email ?? ''}</div>
                        </div>
                    ) : (
                        <div className="text-sm text-gray-700">Loading owner details...</div>
                    )}
                </>
            </StbModal>
        </div>
    );
};

type OppRow = {
    id: string;
    expandable: boolean;

    oppId: string;
    accountId: string;

    Name: string;
    AccountName?: string | null;
    StageName?: string | null;
    Amount?: number | null;
    CloseDate?: string | null;
    se_phase?: string | null;
    SE_Status__c?: string | null;
    SE_Notes__c?: string | null;

    last_updated?: string | null;
    __chip: ChipColor;
};

interface Props {
    email: string;
    /** `all` or `range:YYYY-MM-DD:YYYY-MM-DD` close-date window from the time dropdown. */
    timeFilter?: string;
    /** The fiscal calendar is still loading; hold the request until the window is known. */
    timePending?: boolean;
}

export default function IndividualContributorOppsTable({ email, timeFilter = 'all', timePending = false }: Props) {
    const { data, isLoading, error } = useSfTeamOpportunities(
        email,
        { enabled: !!email && !timePending },
        timeFilter,
    );

    // expanded rows
    const [expanded, setExpanded] = useState<string[]>([]);

    // pagination (0-based)
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // sorting state (for "default" restore)
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('asc');
    const [sortColumnId, setSortColumnId] = useState<string>('CloseDate');

    const opps: any[] = useMemo(() => {
        if (!data || typeof data !== 'object') return [];
        const key = Object.keys(data)[0];
        const block = (data as any)[key];
        return Array.isArray(block?.opportunities) ? block.opportunities : [];
    }, [data]);

    const originalRows: OppRow[] = useMemo(() => {
        return opps.map((o: any) => {
            const oppId = String(o?.id ?? o?.Id ?? '');
            const accountId = String(o?.AccountId ?? '');
            const lastUpdated = o?.last_updated ?? null;

            return {
                id: `opp_${safeRowId(oppId)}`,
                expandable: true,
                oppId,
                accountId,
                Name: String(o?.Name ?? ''),
                AccountName: o?.AccountName ?? null,
                StageName: o?.StageName ?? null,
                Amount: o?.Amount ?? null,
                CloseDate: o?.CloseDate ?? null, // keep as string for date parsing
                se_phase: o?.se_phase ?? null,
                SE_Status__c: o?.SE_Status__c ?? null,
                SE_Notes__c: o?.SE_Notes__c ?? null,
                last_updated: lastUpdated ? String(lastUpdated) : null,
                __chip: getAgeChipColor(lastUpdated),
            };
        });
    }, [opps]);

    // columns: enable sorting + type (as per docs)
    const columns: Column[] = useMemo(() => {
        const base: any[] = [
            {
                id: 'expandable',
                name: '',
                width: 72,
                maxWidth: 72,
                columnType: ColumnType.Custom,
                sortable: false,
                reorderable: false,
                resizable: false,
                pin: ColumnPin.Left,
                align: ColumnAlignment.Center,
                dataType: DataType.String,
            },
            // Percentage widths (sum ~84%) so columns shrink with the 100%-wide table; the trailing
            // width:'100%' filler absorbs any remainder — no horizontal scroll. (expandable stays a
            // fixed pinned 72px.)
            { id: 'Name', name: 'Opportunity', width: '18%', columnType: ColumnType.Custom, sortable: true, type: 'string' },
            { id: 'AccountName', name: 'Account', width: '15%', columnType: ColumnType.Custom, sortable: true, type: 'string' },
            { id: 'StageName', name: 'Stage', width: '12%', columnType: ColumnType.String, sortable: true, type: 'string' },
            { id: 'se_phase', name: 'SE Phase', width: '10%', columnType: ColumnType.String, sortable: true, type: 'string' },
            { id: 'Amount', name: 'Amount', width: '8%', columnType: ColumnType.Number, align: ColumnAlignment.Right, sortable: true, type: 'number' },
            { id: 'CloseDate', name: 'Close Date', width: '9%', columnType: ColumnType.Date, sortable: true, type: 'date' },
            { id: 'SE_Status__c', name: 'SE Status', width: '12%', columnType: ColumnType.String, sortable: true, type: 'string' },
            { id: 'empty', name: '', width: '100%', columnType: ColumnType.Custom, sortable: false },
        ];

        return base.map((c) =>
            getColumnTypeConfig({
                ...c,
                resizable: false,
                reorderable: false,
            })
        );
    }, []);

    // Apply default sort (soonest CloseDate asc) and later sort states
    const sortedRows: OppRow[] = useMemo(() => {
        const arr = [...originalRows];

        const colId = sortColumnId;
        const order = sortOrder;

        if (!order || !colId) {
            // default (restore original fetch order)
            return arr;
        }

        // Default sort behavior: CloseDate asc if set
        // Implement stable custom sort for robustness (and so "default" can restore originalRows)
        const dir = order === 'asc' ? 1 : -1;

        arr.sort((a: any, b: any) => {
            const av = a[colId];
            const bv = b[colId];

            if (colId === 'Amount') {
                const an = Number(av ?? 0);
                const bn = Number(bv ?? 0);
                return (an - bn) * dir;
            }

            if (colId === 'CloseDate') {
                const ad = av ? new Date(av).getTime() : Number.POSITIVE_INFINITY;
                const bd = bv ? new Date(bv).getTime() : Number.POSITIVE_INFINITY;
                return (ad - bd) * dir;
            }

            // string fallback
            return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
        });

        return arr;
    }, [originalRows, sortColumnId, sortOrder]);

    // Paging is applied AFTER sorting
    const totalCount = sortedRows.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const safePageIndex = Math.min(pageIndex, totalPages - 1);

    const pagedRows = useMemo(() => {
        const start = safePageIndex * pageSize;
        return sortedRows.slice(start, start + pageSize);
    }, [sortedRows, safePageIndex, pageSize]);

    const toggleExpanded = useCallback((rowId: string) => {
        setExpanded((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
    }, []);

    const renderHeader = () => {
        // No `w-max`: it forced each header to its single-line max-content width, expanding columns
        // past their percentage share. Let labels wrap/ellipsize within the allotted width instead.
        return columns.map((column: any) => (
            <div key={`column-${column.id}`} slot={`column-${column.id}`}>
                <div
                    className="flex items-center relative"
                    style={{ justifyContent: column.align === ColumnAlignment.Right ? 'flex-end' : 'flex-start' }}
                >
                    <div>{column.name}</div>
                </div>
            </div>
        ));
    };

    const renderBody = () => {
        if (!pagedRows.length) return null;

        const list: JSX.Element[] = [];

        pagedRows.forEach((row) => {
            columns.forEach((column: any) => {
                const cellId = `row-${row.id}-column-${column.id}`;

                if (column.id === 'expandable') {
                    const title =
                        row.last_updated
                            ? `Last updated: ${new Date(row.last_updated).toLocaleString()}`
                            : 'Last updated unknown';

                    list.push(
                        <div key={cellId} slot={cellId} className="min-h-8 flex items-center justify-center">
                            <HbrButton
                                variant="text"
                                size="xsmall"
                                aria={{ label: 'expandable row' }}
                                onClick={(evt: any) => {
                                    evt.stopPropagation();
                                    toggleExpanded(row.id);
                                }}
                            >
                                <HbrIcon
                                    asset={caretRight}
                                    className={expanded.includes(row.id) ? 'hbr-css__expandable-btn-expanded' : ''}
                                />
                            </HbrButton>

                            <span
                                className={`rounded-full ${chipClass(row.__chip)} text-white w-5 h-5 inline-block ml-1`}
                                title={title}
                                aria-label={title}
                            />
                        </div>
                    );
                    return;
                }

                if (column.id === 'Name') {
                    list.push(
                        <div
                            key={cellId}
                            slot={cellId}
                            className="min-w-0 font-semibold hover:text-link hover:underline cursor-pointer min-h-8 flex items-center truncate"
                            title={String(row.Name ?? '')}
                        >
                            <Link className="truncate" to={`/opportunity/${row.oppId}`}>{row.Name}</Link>
                        </div>
                    );
                    return;
                }

                if (column.id === 'AccountName') {
                    list.push(
                        <div
                            key={cellId}
                            slot={cellId}
                            className="min-w-0 font-semibold hover:text-link hover:underline cursor-pointer min-h-8 flex items-center truncate"
                            title={String(row.AccountName ?? '')}
                        >
                            <Link className="truncate" to={`/customer/${row.accountId}`}>{row.AccountName ?? ''}</Link>
                        </div>
                    );
                    return;
                }

                if (column.id === 'Amount') {
                    list.push(
                        <div
                            key={cellId}
                            slot={cellId}
                            className="min-h-8 flex items-center box-content"
                            style={{ justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}
                        >
                            {formatCurrencyUSD(row.Amount)}
                        </div>
                    );
                    return;
                }

                if (column.id === 'CloseDate') {
                    list.push(
                        <div key={cellId} slot={cellId} className="min-h-8 flex items-center box-content">
                            {formatDate(row.CloseDate)}
                        </div>
                    );
                    return;
                }

                if (column.id === 'empty') {
                    list.push(<div key={cellId} slot={cellId} className="min-h-8 flex items-center" />);
                    return;
                }

                const v = (row as any)[column.id];
                list.push(
                    <div key={cellId} slot={cellId} className="min-h-8 flex items-center box-content">
                        {v ?? ''}
                    </div>
                );
            });

            if (row.expandable && expanded.includes(row.id)) {
                const expandableId = `row-${row.id}-expandable`;
                const notes = formatSeNotes(row.SE_Notes__c);

                list.push(
                    <div
                        key={expandableId}
                        slot={expandableId}
                        className="hbr-css__table-child-container"
                        style={{ padding: '10px', width: '100%' }}
                    >
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <div>
                                <div className="hbr-css__text-primary-sm-semibold mb-2">SE Notes</div>
                                {notes.length === 0 ? (
                                    <div className="text-sm text-gray-600 italic">No SE notes</div>
                                ) : (
                                    <div className="max-h-80 overflow-y-auto">
                                        <p className="text-sm">
                                            {notes.map((item, idx) => (
                                                <React.Fragment key={`SENote-${row.id}-${idx}`}>
                                                    <span className="font-bold">{item.date}</span>
                                                    <span>{item.text}</span>
                                                    <br />
                                                </React.Fragment>
                                            ))}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div>
                                <SEWorkLogPanel opportunityId={row.oppId} />
                            </div>
                        </div>

                        <HbrDivider className="mt-4" />
                    </div>
                );
            }
        });

        return list;
    };

    // Sorting handler (pagination+sorting: use event.detail.sortFn on the full dataset)
    const handleSort = (event: any) => {
        const nextOrder = event?.detail?.order as 'asc' | 'desc' | undefined;
        const col = event?.detail?.column;
        const sortFn = event?.detail?.sortFn as undefined | ((rows: any[]) => any[]);

        if (!col?.id) return;

        // Track current sort state so "default" restores originalRows order
        if (!nextOrder) {
            setSortOrder(null);
            setSortColumnId(col.id);
            setExpanded([]);
            setPageIndex(0);
            return;
        }

        // Use internal sortFn for full dataset when available (recommended by docs)
        if (typeof sortFn === 'function') {
            const sorted = sortFn([...originalRows]);
            // We store sort state and let memo recompute sortedRows via custom sorter,
            // BUT to avoid divergence, just mirror the state:
            setSortOrder(nextOrder);
            setSortColumnId(col.id);

            // If you prefer to trust sortFn entirely, you could store sorted[] in state instead.
            // Keeping state-based approach for "default => original" behavior.
            setExpanded([]);
            setPageIndex(0);
            return;
        }

        // Fallback: still update state, memo sorter handles it
        setSortOrder(nextOrder);
        setSortColumnId(col.id);
        setExpanded([]);
        setPageIndex(0);
    };

    // Pagination handlers
    const handleSizeChanged = (e: any) => {
        const nextSize = Number(e.detail.pageSize);
        setPageSize(nextSize);
        setExpanded([]);
        setPageIndex(0);
    };

    const handlePageChanged = (e: any) => {
        setExpanded([]);
        setPageIndex(Number(e.detail));
    };

    // Ensure pageIndex stays valid when data/pageSize changes
    useEffect(() => {
        if (pageIndex !== safePageIndex) setPageIndex(safePageIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safePageIndex]);

    if (isLoading) return <div style={{ padding: 12 }}>Loading opportunities…</div>;
    if (error) return <div style={{ padding: 12, color: 'red' }}>{(error as Error).message}</div>;

    if (!sortedRows.length) {
        return (
            <div className="bg-blue-100 border border-blue-200 text-blue-800 px-4 py-3 rounded-md mb-4 flex items-center gap-2">
                No opportunities found
            </div>
        );
    }

    return (
        <div>
            <HbrDomTable
                autoLayout
                style={{ display: 'block', width: '100%' }}
                expanded={expanded}
                columns={columns as any}
                data={pagedRows}
                onHbr-sort={handleSort}
            >
                {renderHeader()}
                {renderBody()}
            </HbrDomTable>

            {totalCount > pageSize && (
                <div className="py-2">
                    <HbrPagination
                        itemCount={totalCount}
                        pageSize={pageSize}
                        page={safePageIndex}
                        sizeChanger={true}
                        pageInfo={true}
                        pageOptions={[10, 20, 30, 50, 100]}
                        onHbr-size-changed={handleSizeChanged}
                        onHbr-page-changed={handlePageChanged}
                    />
                </div>
            )}
        </div>
    );
}